import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/KeycloakContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import adminService from '../../api/adminService';
import Pagination from '../../ui/Pagination';
import Toast from '../../ui/Toast';
import './ActivityLog.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const ACTION_LABELS = {
  CREATE_PROSPECT: { label: 'Search', icon: 'fa-search', color: '#3b82f6' },
  CREATE_PROSPECT_ENRICHMENT: { label: 'Enrichment', icon: 'fa-id-badge', color: '#22c55e' },
  INVITE_USER: { label: 'Invite User', icon: 'fa-user-plus', color: '#f59e0b' },
  JOIN_WORKSPACE: { label: 'Join Workspace', icon: 'fa-door-open', color: '#10b981' },
  CHANGE_ROLE: { label: 'Change Role', icon: 'fa-user-tag', color: '#3b82f6' },
  MARKET_ANALYSIS_SEARCH: { label: 'Market Analysis', icon: 'fa-chart-bar', color: '#06b6d4' },
  B2C_RECOMMENDATION_START: { label: 'Search Persona', icon: 'fa-user-secret', color: '#8b5cf6' },
  REMOVE_USER: { label: 'Remove Member', icon: 'fa-user-minus', color: '#ef4444' },
  CREATE_WORKSPACE: { label: 'Create Workspace', icon: 'fa-building', color: '#3b82f6' },
  BULK_EMAIL_OUTREACH: { label: 'Email Outreach', icon: 'fa-envelope-open-text', color: '#f97316' },
  REVEAL_CONTACT: { label: 'Reveal Contact', icon: 'fa-eye', color: '#8b5cf6' },
  USER_LOGIN: { label: 'User Login', icon: 'fa-sign-in-alt', color: '#10b981' },
  USER_LOGOUT: { label: 'User Logout', icon: 'fa-sign-out-alt', color: '#64748b' },
};

function formatTimestamp(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatMetadataKey(key) {
  let formatted = key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim();
  formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1);
  return formatted.replace(/\s+Id$/i, '');
}

function ActionBadge({ actionType }) {
  const info = ACTION_LABELS[actionType] || {
    label: actionType,
    icon: 'fa-circle',
    color: '#94a3b8',
  };
  return (
    <span
      className="al-badge"
      style={{ color: info.color, borderColor: info.color + '33', background: info.color + '11' }}
    >
      <i className={`fas ${info.icon}`}></i>
      {info.label}
    </span>
  );
}

export default function ActivityLog({ embedded }) {
  const { keycloak } = useAuth();
  const { isOrganizationAdmin, isSuperadmin, workspaceId } = useWorkspace();

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });

  // Filters
  const [actionType, setActionType] = useState('');
  const [entityType, setEntityType] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterWorkspaceName, setFilterWorkspaceName] = useState('');

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Sorting
  const [sort, setSort] = useState({ sortBy: 'created_at', sortOrder: 'DESC' });

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pageInput, setPageInput] = useState('1');

  const [members, setMembers] = useState([]);

  // Helper to reset pagination when filters change
  const resetPaginationAndFetch = useCallback(() => {
    setPage(1);
  }, []);

  const fetchMembers = useCallback(async () => {
    if (embedded && isSuperadmin && keycloak?.token) {
      try {
        const data = await adminService.getUsers();
        setMembers(
          data.map((u) => ({
            user_id: u.id,
            email: u.email,
            username: u.username || null,
            display_name: u.username || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
          }))
        );
        return;
      } catch (err) {
        console.error('Failed to fetch admin users for activity log:', err);
      }
    }

    if (!workspaceId || !keycloak?.token) return;
    try {
      const res = await fetch(`${API_URL}/workspace/${workspaceId}/members`, {
        headers: { Authorization: `Bearer ${keycloak.token}` },
      });
      if (res.ok) {
        const json = await res.json();
        if (json.success) setMembers(json.members || []);
      }
    } catch (err) {
      console.error('Failed to fetch members for activity log filter:', err);
    }
  }, [workspaceId, keycloak?.token, embedded, isSuperadmin]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const fetchLogs = useCallback(async () => {
    if (!keycloak?.token) return;
    setLoading(true);
    setToast({ ...toast, show: false });
    try {
      const params = new URLSearchParams({
        page,
        limit,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder,
      });
      if (!embedded && workspaceId) params.set('workspace_id', workspaceId);
      if (embedded && isSuperadmin && filterWorkspaceName)
        params.set('workspace_name', filterWorkspaceName);
      if (!embedded && filterUserId) params.set('user_id', filterUserId);
      if (actionType) params.set('action_type', actionType);
      if (entityType) params.set('entity_type', entityType);

      if (dateFrom) params.set('from', new Date(dateFrom).toISOString());
      if (dateTo) params.set('to', new Date(dateTo + 'T23:59:59').toISOString());

      const res = await fetch(`${API_URL}/activity-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${keycloak.token}` },
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const json = await res.json();
      if (json.success) {
        setLogs(json.data);
        setTotal(json.total || 0);
      } else {
        throw new Error(json.error || 'Failed to load logs');
      }
    } catch (err) {
      setToast({ show: true, message: err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [
    keycloak?.token,
    page,
    limit,
    actionType,
    entityType,
    dateFrom,
    dateTo,
    workspaceId,
    filterUserId,
    filterWorkspaceName,
    embedded,
    isSuperadmin,
    sort,
  ]);

  // Fetch logs when page changes
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs, page]);

  // Update page input when page changes
  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  // Reset to page 1 when filters change
  const applyFilter = () => {
    setPage(1);
    fetchLogs();
  };

  const handleSort = (field) => {
    const isAsc = sort.sortBy === field && sort.sortOrder === 'ASC';
    setSort({
      sortBy: field,
      sortOrder: isAsc ? 'DESC' : 'ASC',
    });
    setPage(1);
  };

  const getSortIcon = (field) => {
    if (sort.sortBy !== field)
      return (
        <i
          className="fas fa-sort"
          style={{ marginLeft: '4px', opacity: 0.3, fontSize: '10px' }}
        ></i>
      );
    return sort.sortOrder === 'ASC' ? (
      <i
        className="fas fa-sort-up"
        style={{ marginLeft: '4px', color: '#3b82f6', fontSize: '10px' }}
      ></i>
    ) : (
      <i
        className="fas fa-sort-down"
        style={{ marginLeft: '4px', color: '#3b82f6', fontSize: '10px' }}
      ></i>
    );
  };

  const totalPages = Math.ceil(total / limit);

  // Access guard
  if (!isOrganizationAdmin && !isSuperadmin && !embedded) {
    return (
      <div className="al-page">
        <div className="al-access-denied">
          <i className="fas fa-lock"></i>
          <h3>Access Restricted</h3>
          <p>Only organization admins can view activity logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`al-page ${embedded ? 'embedded' : ''}`}>
      {/* Header */}
      {!embedded && (
        <div className="tm-tabs-row" style={{ marginBottom: '14px' }}>
          <div className="tm-tabs">
            <button className="tm-tab active" style={{ cursor: 'default' }}>
              <i className="fas fa-history"></i>
              Activity Logs ({total.toLocaleString()} total events)
            </button>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="al-filter-bar">
        <div className="al-filter-group">
          <label>Action</label>
          <select
            value={actionType}
            onChange={(e) => {
              setActionType(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All actions</option>
            {Object.entries(ACTION_LABELS)
              .filter(([k]) => k !== 'USER_LOGIN' && k !== 'USER_LOGOUT')
              .map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
          </select>
        </div>

        <div className="al-filter-group">
          <label>From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="al-filter-group">
          <label>To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="al-filter-group" style={{ minWidth: '118px', flex: '0 0 118px' }}>
          <label style={{ opacity: 0 }}>&nbsp;</label>
          <button
            className="tm-btn tm-btn-outline"
            style={{
              width: '100%',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => {
              setActionType('');
              setFilterUserId('');
              setFilterWorkspaceName('');
              setDateFrom('');
              setDateTo('');
              setSort({ sortBy: 'created_at', sortOrder: 'DESC' });
              setPage(1);
            }}
          >
            <i className="fas fa-times"></i> Clear
          </button>
        </div>
      </div>

      {/* Alerts */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {/* Table & Pagination */}
      <Pagination
        columns={[
          {
            key: 'user_id',
            label: 'User',
            sortable: true,
            width: embedded && isSuperadmin ? '20%' : '25%',
          },
          { key: 'action_type', label: 'Action', width: embedded && isSuperadmin ? '18%' : '22%' },
          {
            key: 'created_at',
            label: 'Timestamp',
            sortable: true,
            width: embedded && isSuperadmin ? '17%' : '20%',
          },
          ...(embedded && isSuperadmin
            ? [{ key: 'workspace_name', label: 'Workspace', sortable: true, width: '20%' }]
            : []),
          { key: 'details', label: 'Details', width: embedded && isSuperadmin ? '25%' : '33%' },
        ]}
        data={logs}
        loading={loading}
        renderRow={(log) => {
          const actor = members.find((m) => m.user_id === log.user_id);
          let actorName = 'system';
          if (log.user_id) {
            const metaName =
              log.metadata?.userName || log.metadata?.username || log.metadata?.displayName;
            const metaEmail =
              log.metadata?.userEmail || log.metadata?.actorEmail || log.metadata?.email;

            if (actor) {
              actorName = actor.username || actor.display_name || actor.email || log.user_id;
            } else {
              actorName = metaName || metaEmail || 'unknown';
            }

            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            if (uuidRegex.test(String(actorName))) {
              actorName = metaName || metaEmail || 'unknown';
            }
          }

          actorName = String(actorName).toLowerCase();

          return (
            <tr key={log.id}>
              <td>
                <span
                  className="al-actor-name"
                  style={{
                    fontWeight: 500,
                    color: '#334155',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                  }}
                >
                  {actorName}
                </span>
              </td>
              <td style={{ overflow: 'hidden' }}>
                <ActionBadge actionType={log.action_type} />
              </td>
              <td className="al-ts">{formatTimestamp(log.created_at)}</td>
              {embedded && isSuperadmin && (
                <td>
                  <span className="tm-org-text" style={{ fontWeight: 500 }}>
                    {log.workspace_name ||
                      log.metadata?.workspaceName ||
                      log.workspace_id ||
                      'System'}
                  </span>
                </td>
              )}
              <td>
                {log.metadata && Object.keys(log.metadata).length > 0 ? (
                  <details className="al-meta-details">
                    <summary>View</summary>
                    <div className="al-meta-list">
                      {(() => {
                        const entries = Object.entries(log.metadata).filter(([key, value]) => {
                          if (value === null || value === undefined || value === '') return false;
                          const lk = key.toLowerCase();
                          if (
                            lk === 'workspacename' ||
                            lk === 'workspaceid' ||
                            lk === 'organizationname' ||
                            lk === 'organizationid'
                          )
                            return false;
                          if (lk === 'invite' || lk === 'inviteid' || lk === 'invite_id')
                            return false;
                          if (key.toLowerCase().endsWith('id') && key.length > 2) {
                            const prefix = key.slice(0, -2).toLowerCase();
                            const hasBetterField = Object.keys(log.metadata).some((k) => {
                              const lk = k.toLowerCase();
                              return (
                                lk === prefix + 'name' ||
                                lk === prefix + 'email' ||
                                lk === 'email' ||
                                lk === 'name' ||
                                lk === prefix
                              );
                            });
                            if (hasBetterField) return false;
                          }
                          return true;
                        });

                        if (entries.length === 0)
                          return (
                            <div className="al-meta-item">
                              <span className="al-meta-value" style={{ color: '#64748b' }}>
                                No details available
                              </span>
                            </div>
                          );

                        return entries.map(([key, value]) => {
                          let displayValue = value;
                          const lk = key.toLowerCase();
                          if (lk === 'role') {
                            const valLower = String(value).toLowerCase();
                            if (
                              valLower.includes('organization_user') ||
                              valLower.includes('org_member') ||
                              valLower === 'member'
                            )
                              displayValue = 'Member';
                            else if (
                              valLower.includes('organization_admin') ||
                              valLower.includes('org_admin') ||
                              valLower === 'admin'
                            )
                              displayValue = 'Admin';
                            else if (valLower === 'superadmin') displayValue = 'Superadmin';
                            else
                              displayValue =
                                value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
                          } else if (lk === 'joinedvia' || lk === 'joined_via') {
                            if (value === 'invitation') displayValue = 'Invitation';
                            else if (value === 'direct_signup') displayValue = 'Direct Signup';
                            else
                              displayValue =
                                value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
                          } else if (typeof value === 'object') {
                            displayValue = JSON.stringify(value);
                          }
                          return (
                            <div key={key} className="al-meta-item">
                              <span className="al-meta-key">{formatMetadataKey(key)}:</span>
                              <span className="al-meta-value">{String(displayValue)}</span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </details>
                ) : (
                  <span className="al-dash">—</span>
                )}
              </td>
            </tr>
          );
        }}
        emptyState={
          <>
            <i className="fas fa-inbox"></i>
            <p>No activity logs found for the selected filters.</p>
          </>
        }
        onSort={handleSort}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        itemsPerPage={limit}
        onItemsPerPageChange={(newLimit) => {
          setLimit(newLimit);
          setPage(1);
        }}
        totalItems={total}
        itemsName="records"
        className="al-table-wrap"
      />
    </div>
  );
}
